import * as Sentry from '@sentry/react';

import { computed, makeObservable } from 'mobx';

import { Model } from '../../core/engine';

import { ManyToOne } from '../../core/engine/decorators';
import { Project } from './project';
import { Property } from './property';

import { api } from '../../api';
import { createFileName } from '../../core/services/file.service';
import { BufferToBlob } from '../../lib/utils/BufferToBolb';

export interface ViewProperty {
  propertyId: string;

  order: number;

  isVisible?: boolean;

  columnWidth?: number;
}

export class View extends Model {
  name: string;

  projectId: string;

  properties: ViewProperty[];

  @ManyToOne('views')
  project: Project;

  options: {
    showBanner: boolean;
  };

  constructor() {
    super('shot-lists');

    makeObservable(this, {
      properties: true,
      visibleProperties: computed,
      allProperties: computed,
      options: true,
    });
  }

  async download(type: 'pdf' | 'csv'): Promise<boolean> {
    try {
      const { data } = await api.get(`/shot-lists/${this._id}`, {
        responseType: 'arraybuffer',
        headers: {
          Accept: type === 'pdf' ? 'application/pdf' : 'text/csv',
        },
      });
      const fileName = createFileName(
        this.project.name,
        this.name,
        type === 'pdf' ? 'pdf' : 'csv',
        false,
        true,
      );
      BufferToBlob(data, fileName);
      return true;
    } catch (e) {
      Sentry.captureException(e);
      return false;
    }
  }

  override toPOJO(): Record<string, any> {
    throw new Error('Method not implemented.');
  }

  get allProperties() {
    return this.project.properties
      .map((property, index) => {
        const meta = this.properties.find((el) => el.propertyId === property._id);

        /**
         * If it's not in the view at all, it should not appear in the list
         * e.g. in the storyboard we cannot display estimated time
         */
        if (!meta) {
          return;
        }

        return Object.assign(property, meta, {
          order: meta?.order || index,
        });
      })
      .filter((property) => !!property);
  }

  get visibleProperties() {
    return this.project.properties
      .map((property, index) => {
        const meta = this.properties.find((el) => el.propertyId === property._id);

        /**
         * If it's not in the view at all, it should not appear in the list
         * e.g. in the storyboard we cannot display estimated time
         */
        if (!meta) {
          return;
        }

        if (meta?.isVisible !== false) {
          return Object.assign(property, meta, {
            order: meta?.order || index,
          });
        }
      })
      .filter((property) => !!property)
      .sort((a, b) => (a.order > b.order ? 1 : -1));
  }

  /**
   * Add a property to the view to mirror BE when update when adding a property
   * @param property
   */
  addProperty(property: Property) {
    this.properties = this.properties.concat({
      propertyId: property._id,
      isVisible: true,
      order: this.project.properties.length + 1,
    });
  }
}
